import { useEffect, useRef, useState } from 'react'
import { useLocation, Link } from 'react-router-dom'
import { LogOut, ExternalLink, ChevronDown, UserCog } from 'lucide-react'
import { NAV, isGroup, isLabel } from '../../lib/nav'
import { useAuth } from '../../store/auth'
import { cn } from '../../lib/cn'

function titleForPath(pathname: string): string {
  for (const e of NAV) {
    if (isGroup(e)) {
      const hit = e.items.find((i) => i.to === pathname)
      if (hit) return `${e.label} · ${hit.label}`
    } else if (!isLabel(e) && e.to === pathname) {
      return e.label
    }
  }
  return 'Gestionale'
}

export function Topbar() {
  const { pathname } = useLocation()
  const user = useAuth((s) => s.user)
  const logout = useAuth((s) => s.logout)

  return (
    <header className="flex h-14 shrink-0 items-center justify-between border-b border-ink-200 bg-white px-5">
      <h1 className="text-[15px] font-semibold text-ink-900">{titleForPath(pathname)}</h1>
      <div className="flex items-center gap-3">
        <a
          href="https://booking.garagesanmarco.it/admin/"
          target="_blank"
          rel="noreferrer"
          className="btn btn-ghost text-2xs"
          title="Apri il gestionale legacy (produzione)"
        >
          <ExternalLink className="h-3.5 w-3.5" /> Legacy
        </a>
        <UserMenu user={user} onLogout={() => logout()} />
      </div>
    </header>
  )
}

function UserMenu({
  user,
  onLogout,
}: {
  user: { name?: string; email?: string } | null | undefined
  onLogout: () => void
}) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    function onDoc(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [open])

  return (
    <div ref={ref} className="relative border-l border-ink-200 pl-3">
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2.5 rounded-lg px-1.5 py-1 transition-colors hover:bg-ink-50"
        title="Account"
      >
        <div className="text-right leading-tight">
          <p className="text-[13px] font-semibold text-ink-800">{user?.name ?? '—'}</p>
          <p className="text-2xs text-ink-400">{user?.email}</p>
        </div>
        <span className="flex h-8 w-8 items-center justify-center rounded bg-ink-900 text-xs font-bold text-white">
          {(user?.name ?? 'G').slice(0, 1).toUpperCase()}
        </span>
        <ChevronDown className={cn('h-4 w-4 text-ink-400 transition-transform', open && 'rotate-180')} />
      </button>

      {open && (
        <div className="absolute right-0 top-full z-50 mt-1.5 w-56 overflow-hidden rounded-xl border border-ink-200 bg-white py-1 shadow-lg">
          <div className="border-b border-ink-100 px-3.5 py-2.5">
            <p className="truncate text-[13px] font-semibold text-ink-800">{user?.name ?? '—'}</p>
            <p className="truncate text-2xs text-ink-400">{user?.email}</p>
          </div>
          <Link
            to="/account"
            onClick={() => setOpen(false)}
            className="flex items-center gap-2.5 px-3.5 py-2.5 text-[13px] font-medium text-ink-700 hover:bg-ink-50"
          >
            <UserCog className="h-4 w-4 text-ink-400" /> Il mio profilo
          </Link>
          <div className="my-1 border-t border-ink-100" />
          <button
            onClick={() => {
              setOpen(false)
              onLogout()
            }}
            className="flex w-full items-center gap-2.5 px-3.5 py-2.5 text-left text-[13px] font-medium text-venetian hover:bg-venetian-soft"
          >
            <LogOut className="h-4 w-4" /> Esci
          </button>
        </div>
      )}
    </div>
  )
}
